import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface TwoFactorVerificationProps {
  onVerify: (code: string) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
  title?: string;
  subtitle?: string;
}

export function TwoFactorVerification({
  onVerify,
  onCancel,
  isLoading = false,
  error = null,
  title = "Two - factor authentication",
  subtitle = "Enter the 6 figure confirmation code shown on the app"
}: TwoFactorVerificationProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...code];
      
      for (let i = 0; i < pastedCode.length && i + index < 6; i++) {
        if (/^\d$/.test(pastedCode[i])) {
          newCode[index + i] = pastedCode[i];
        }
      }
      
      setCode(newCode);
      
      // Focus last filled input or next empty one
      const nextIndex = Math.min(index + pastedCode.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex]?.focus();
      }
      return;
    }

    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      await onVerify(fullCode);
    }
  };

  const isCodeComplete = code.every(digit => digit !== '');

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/img/logo.png" 
              alt="LogisHaus" 
              className="h-12 mr-3"
            />
            <span className="text-2xl font-bold text-gray-900">LogisHaus</span>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        </div>

        {/* Animation */}
        <div className="text-center mb-6">
          <img 
            src="/img/2faanim.gif" 
            alt="Two-factor authentication illustration" 
            className="w-48 h-48 mx-auto"
          />
          <p className="text-gray-600 text-sm mt-4">{subtitle}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Code Input */}
        <div className="mb-8">
          <div className="flex justify-center space-x-3 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:border-yellow-400 focus:outline-none transition-colors"
                disabled={isLoading}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 focus:outline-none transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={!isCodeComplete || isLoading}
            className="flex-1 bg-yellow-400 text-black py-3 px-4 rounded-lg font-medium hover:bg-yellow-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Verifying...
              </div>
            ) : (
              <span className="underline">Submit</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
